import { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAccount } from '../../contexts/Account';
import ACSnackbar from '../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import {
  aobfStep4Mobile,
} from '../../services/company_services';

const styles = () => ({
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

function CompanyOnboardingStep4() {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const history = useHistory();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = styles();

  const [currentGoal, setCurrentGoal] = useState(
    accountDetails.currentGoal || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const disableButton = () => isLoading || !currentGoal.trim();

  async function sendFormData() {
    try {
      await aobfStep4Mobile({
        CONTACT_ID: asLoggedUserDetails.userId,
        CURRENT_GOAL: currentGoal.trim(),
      });

      return {
        currentGoal: currentGoal.trim(),
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
      return {};
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const [payload] = await Promise.all([
        sendFormData(),
      ]);

      editAccountInformation({ ...payload });
      history.push('/company/onboarding/step5');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 4/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(4 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#000000',
          mb: 1,
        }}
      >
        What do you need advice on?
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.43px',
          color: '#646D7A',
          mb: 3,
        }}
      >
        What is your current main goal, challenge, or question you'd like to get
        input on from your advisory board?
      </Typography>

      <Grid>
        <Grid {...classes.gridItemProps} mb="16px">
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={7}
            placeholder="e.g. We are struggling with regulations and compliance as we scale internationally, does anyone have ideas on how to overcome?"
            value={currentGoal}
            onChange={(e) => setCurrentGoal(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F9FAFB',
                borderRadius: '14px',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
                '&:hover fieldset': {
                  borderColor: '#CBD5E1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3171F6',
                  boxShadow: '0 0 0 2px rgba(49, 113, 246, 0.15)',
                },
              },
            }}
            inputProps={{ maxLength: 300 }}
          />
        </Grid>

        <Grid {...classes.gridItemProps} mb="16px">
          <Box
            sx={{
              backgroundColor: '#F7F9FC',
              border: '1px solid #E0E7F1',
              borderRadius: '12px',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography
              sx={{ fontSize: '14px', color: '#4B5563', fontWeight: 500 }}
            >
              💡 Advisors will weigh in and submit ideas
            </Typography>
            <Typography
              sx={{ fontSize: '14px', color: '#4B5563', fontWeight: 500 }}
            >
              📝 We recommend using 1-2 concise sentences
            </Typography>
            <Typography
              sx={{ fontSize: '14px', color: '#4B5563', fontWeight: 500 }}
            >
              🔗 You can include links to additional materials
            </Typography>
          </Box>
        </Grid>

        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={disableButton()}
          sx={{
            borderRadius: '15px',
            backgroundColor: '#3171F6',
            '&:hover': {
              backgroundColor: '#2A60E0',
            },
            height: '52px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 700,
            width: '100%',
            mb: isSM ? '24px' : null,
            my: '20px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
}

export default CompanyOnboardingStep4;
